import Loader from '@shared/loader'
import MaterialTable from '@material-table/core'
import Permission from '@constants/permission'
import ProductStoresDialog from '@components/shared/dialogs/product-stores-dialog'
import React, { ReactElement, useMemo, useState } from 'react'
import hasPerms from '@utils/has-permission'
import useAllActions from '@hooks/use-all-actions'
import useSelector from '@hooks/use-selector'
import { getStoreList } from '@queries/stores'
import { selectProductVariants } from '@store/selectors/variants'
import { useConfirm } from '@components/shared/confirm-dialog'
import { useQuery } from '@tanstack/react-query'

interface Props {
  productsStores: any
  product: any
  setStoreVariantPrice(store_id, variant_id, price): Promise<any>
  addProductToStores(storeIds: number[]): Promise<any>
  removeProductFromStores(storeIds: number[]): Promise<any>
  removeProductStore(any): Promise<any>
  addToAllStores(): any
  removeFromAllStores(): any
}

const ProductsStoresTable = ({
  productsStores,
  addProductToStores,
  removeProductFromStores,
  setStoreVariantPrice,
  removeProductStore,
  product,
  addToAllStores,
  removeFromAllStores,
}: Props): ReactElement => {
  const [showDialog, setShowDialog] = useState(false)
  const [showDialogLoading, setShowDialogLoading] = useState(false)
  const { setFlashMessage } = useAllActions()
  const confirm = useConfirm()
  const variants = useSelector(selectProductVariants, product.id)

  const { isLoading: loadingStores, data: stores } = useQuery({
    queryKey: ['storeList'],
    queryFn: getStoreList,
    staleTime: 30000,
  })

  // To add products to stores from the product page you need to be assigned to
  // access ALL stores
  const editable =
    hasPerms(Permission.STORES_UPDATE_ALL) &&
    hasPerms(Permission.STORES_UPDATE_MENU)

  const tableData = useMemo(() => {
    const parents = productsStores.map((p) => ({
      id: p.store_id,
      storeId: p.store_id,
      name: stores?.find((store) => store.id === p.store_id)?.name,
      sku: null,
      price: null,
    }))
    return parents
      .concat(
        productsStores.map((p) => {
          return variants.map((variant) => ({
            id: `v${variant.id}`,
            name: null,
            variantId: variant.id,
            storeId: p.store_id,
            sku: variant.sku,
            parentId: p.store_id,
            price: p.prices.find((x) => x.variant_id == variant.id)?.price,
          }))
        }),
      )
      .flat()
  }, [productsStores, stores, variants])

  const existingStoreIds = useMemo(
    () => productsStores.map((p) => p.store_id),
    [productsStores],
  )

  const applyChanges = async (toAdd: number[], toRemove: number[]) => {
    setShowDialogLoading(true)

    // Tracking the actual number removed
    let removed: number[] = []

    try {
      // Removals first in case there is an ID overlapping bug
      if (toRemove.length > 0) removed = await removeProductFromStores(toRemove)
      if (toAdd.length > 0) await addProductToStores(toAdd)
    } catch (error: any) {
      setShowDialogLoading(false)
      return setFlashMessage(error.message, 'error')
    }

    setShowDialogLoading(false)
    setShowDialog(false)

    const message = [
      toAdd.length > 0 && `Added to ${toAdd.length} stores`,
      toRemove.length > 0 && `Removed from ${removed.length} stores`,
    ]
      .filter(Boolean)
      .join(', ')

    // Let them know if we couldn't remove from as many stores as expected
    if (removed.length < toRemove.length) {
      return setFlashMessage(
        `${message}. ${
          toRemove.length - removed.length
        } could not be removed and are still on the menu.`,
        'error',
      )
    }

    setFlashMessage(message, 'success')
  }

  const saveStores = (storeIds: number[]) => {
    const toAdd = storeIds.filter((id) => !existingStoreIds.includes(id))
    const toRemove = existingStoreIds.filter((id) => !storeIds.includes(id))

    if (toRemove.length > 0) {
      return confirm({
        title: 'Be Careful',
        message: `This will immediately remove this product from ${
          toRemove.length
        } ${toRemove.length === 1 ? 'store menu' : 'store menus'}${
          toAdd.length > 0 ? ` and add it to ${toAdd.length}` : ''
        }. Are you sure?`,
        onConfirm: () => applyChanges(toAdd, toRemove),
      })
    }

    applyChanges(toAdd, toRemove)
  }

  if (loadingStores) return <Loader />

  return (
    <>
      <ProductStoresDialog
        existingStoreIds={existingStoreIds}
        loading={showDialogLoading}
        onClose={() => setShowDialog(false)}
        onConfirm={saveStores}
        open={showDialog}
      />
      <MaterialTable
        actions={
          editable
            ? [
                {
                  tooltip: 'Edit Product Stores',
                  icon: 'storefront',
                  isFreeAction: true,
                  onClick: () => setShowDialog(true),
                },
                {
                  tooltip: 'Add Product to all Stores',
                  icon: 'dynamic_feed',
                  isFreeAction: true,
                  onClick: addToAllStores,
                },
                {
                  tooltip: 'Remove Product from all Stores',
                  icon: 'delete_sweep',
                  isFreeAction: true,
                  onClick: removeFromAllStores,
                },
              ]
            : []
        }
        columns={[
          {
            title: 'Store',
            field: 'name',
            editable: 'never',
          },
          {
            title: 'SKU',
            field: 'sku',
            editable: 'never',
          },
          {
            title: 'Price (cents)',
            field: 'price',
          },
        ]}
        data={tableData}
        editable={
          editable
            ? {
                isEditHidden: (x) => !x.parentId,
                isDeleteHidden: (x) => x.parentId,
                onRowUpdate: async ({ price, storeId, variantId }: any) => {
                  // The row update will set it to a string
                  const newPrice = parseInt(price, 10)
                  if (isNaN(newPrice)) {
                    return
                  } else {
                    await setStoreVariantPrice(storeId, variantId, newPrice)
                      .then(() => setFlashMessage('Price updated.', 'success'))
                      .catch((e) => setFlashMessage(e.message, 'error'))
                  }
                },
                onRowDelete: async ({ storeId }: any) => {
                  await removeProductStore(storeId)
                    .then(() => setFlashMessage('Success', 'success'))
                    .catch((e) => setFlashMessage(e.message, 'error'))
                },
              }
            : {}
        }
        options={{
          actionsColumnIndex: 4,
        }}
        parentChildData={(row, rows) => rows.find((a) => a.id === row.parentId)}
        title='Stores'
      />
    </>
  )
}

export default ProductsStoresTable
